// Default SVG import resolves to a URL string under current Storybook webpack config.
// Use it as an <img src> instead of attempting to render as a React component.
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router';
import avatarUrl from '../../assets/icons/avatar.svg';
import { IconButton } from '../button/button';

export type ProfileNavItem = {
  name: string;
  to?: string;
  action?: () => void;
  authorized?: boolean;
};

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  hasAdminAccess: boolean;
  fullName?: string;
};

export type ProfileProps = {
  user?: User;
  imageSrc?: string;
  items: ProfileNavItem[];
  collapsedMode?: boolean;
  onClick?: (state: boolean) => void;
};

const Profile = memo(({ user, items, imageSrc, collapsedMode, onClick }: ProfileProps) => {
  const [isOpen, setOpen] = useState(false);
  const contentRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    collapsedMode && setOpen(false);
  }, [collapsedMode]);

  const onClickHandler = () => {
    onClick && onClick(false);
    collapsedMode && setOpen(true);
  };

  const resolveUserValue = (user: User) => {
    if (user.fullName) {
      return user.fullName;
    } else {
      return `${user.firstName} ${user.lastName}`;
    }
  };

  const containerStyles = clsx('transition-colors ease duration-300', {
    'bg-gray-450 dark:bg-gray-3': isOpen,
  });
  const avatarStyles = clsx('mr-2', { 'cursor-pointer': collapsedMode });
  const profileMenuStyles = { height: `${isOpen ? contentRef.current?.offsetHeight : 0}px` };
  const angleButtonStyles = clsx('transition-transform duration-100 px-2 py-2 ml-auto', {
    'rotate-180': !isOpen,
  });

  return (
    <div className={containerStyles}>
      <div className="flex items-center py-2 pl-3">
        <div className={avatarStyles} onClick={() => onClickHandler()}>
          {imageSrc ? (
            <img src={imageSrc} width={40} alt="profile image" className="rounded-full" />
          ) : (
            <img src={avatarUrl} width={40} alt="profile image" className="rounded-full" />
          )}
        </div>
        {!collapsedMode && (
          <>
            <div className="overflow-hidden text-ellipsis text-sm">{user && resolveUserValue(user)}</div>
            <IconButton classNames={angleButtonStyles} onClick={() => setOpen(!isOpen)} icon="ChevronUp" />
          </>
        )}
      </div>

      <div className="ease overflow-hidden transition-all" style={profileMenuStyles}>
        <ul ref={contentRef}>
          {items.map((item) => {
            if (item.authorized !== false) {
              return (
                <li
                  key={item.name}
                  className="flex h-9 w-full items-center text-sm transition-colors duration-300 hover:bg-primary-4 dark:hover:bg-gray-4"
                  onClick={() => setOpen(false)}
                >
                  {item.to ? (
                    <NavLink className="flex w-full px-5" to={item.to}>
                      {item.name}
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      className="flex w-full px-5 bg-transparent border-none cursor-pointer text-inherit"
                      onClick={item.action}
                    >
                      {item.name}
                    </button>
                  )}
                </li>
              );
            }
          })}
        </ul>
      </div>
    </div>
  );
});

Profile.displayName = 'User';
export default Profile;
